// Rect, Next
import { useContext, useState } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'
import Link from 'next/link'

// import { useTranslation } from 'next-i18next'

// MUI
import MenuIcon from '@mui/icons-material/Menu'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import IconButton from '@mui/material/IconButton'
import Badge from '@mui/material/Badge'
import LogoutIcon from '@mui/icons-material/Logout'
import LoginIcon from '@mui/icons-material/Login'
import OrdersIcon from '@mui/icons-material/List'
import { Button, Divider, Menu, MenuItem, Typography } from '@mui/material'

// Img
import KroemtechLogo from 'public/img/logos/kroemtech/kroemtech_logo_white.svg'

// Custom
import breakpoints from 'assets/theme/breakpoints'
import MainMenu from 'components/headers/main/MainMenu'
import LanguageSwitcher from 'components/tools/buttons/LanguageSwitcher/LanguageSwitcher'
import MenuSideBar from 'components/sidebars/MenuSideBar'
import { GlobalContext } from 'context/GlobalContextProvider'

// Config
import i18nextConfig from 'next-i18next.config'

// Services
import AuthService from 'services/AuthService'

// Context
import { useCart } from 'context/CartContext'

// Functions
import { isName } from '@/assets/functions/validation'

// Style
import classes from './MainHeader-CSS'
import themeColors from '@/assets/theme/colors'

export default function MainHeader() {
  const { screenDimensions } = useContext(GlobalContext)
  const { firstName } = AuthService.loadCookies()
  const [anchorEl, setAnchorEl] = useState(null)
  const isMenuOpen = Boolean(anchorEl)
  const router = useRouter()
  const currentLocale = router.query.locale || i18nextConfig.i18n.defaultLocale
  const { cartCount } = useCart()

  const [drawerOpen, setDrawerOpen] = useState(false)

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleProfileMenuOpen = (event: any) => {
    event.preventDefault()
    setAnchorEl(event.currentTarget)
  }

  const handleLogout = async () => {
    AuthService.logoutAccount()
    handleMenuClose()
  }

  const getLangButtons = () => (
    <Box>
      <LanguageSwitcher currentLocale={currentLocale} />
    </Box>
  )

  const menuId = 'account-menu'
  const renderMenu = (
    <Menu
      sx={{ marginTop: '50px' }}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <Typography style={classes.menuTitle}>Welcome!</Typography>
      <Divider component="div" style={classes.dividerRoot} />
      {firstName && (
        <Box
          display="flex"
          alignItems="center"
          component={MenuItem}
          onClick={() => router.push(`/${currentLocale}/orders`)}
          sx={classes.menuItem}
        >
          <Box
            component={OrdersIcon}
            width="1.25rem"
            height="1.25rem"
            marginRight="1rem"
          />
          <span>My Orders</span>
        </Box>
      )}

      {firstName ? (
        <Box
          display="flex"
          alignItems="center"
          component={MenuItem}
          onClick={handleLogout}
          sx={classes.menuItem}
        >
          <Box
            component={LogoutIcon}
            width="1.25rem"
            height="1.25rem"
            marginRight="1rem"
          />
          <span>Logout</span>
        </Box>
      ) : (
        <Box
          display="flex"
          alignItems="center"
          component={MenuItem}
          onClick={() => {
            localStorage.setItem('redirectAfterLogin', router.asPath)
            router.push(`/${currentLocale}/login`)
          }}
          sx={classes.menuItem}
        >
          <Box
            component={LoginIcon}
            width="1.25rem"
            height="1.25rem"
            marginRight="1rem"
          />
          <span>Login</span>
        </Box>
      )}
    </Menu>
  )

  const validateName = () => {
    let name = ''
    if (firstName !== undefined) name = firstName
    if (name.length > 14) return `${name.substring(0, 12)}...`
    if (!isName(name)) return 'Login'
    return name
  }

  return (
    <>
      <SwipeableDrawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
        sx={{
          // The drawer's own panel paints its background and full height.
          '& .MuiDrawer-paper': {
            background: themeColors.kroemtech.background,
            backgroundImage: 'none'
          }
        }}
      >
        <MenuSideBar setDrawerOpen={(state: boolean) => setDrawerOpen(state)} />
      </SwipeableDrawer>

      <AppBar sx={classes.appbarStyle}>
        <Toolbar disableGutters>
          <Box sx={classes.mainBox}>
            <Box
              sx={classes.logoBox}
              style={{
                marginLeft:
                  screenDimensions.width >= breakpoints.desktopToMobile
                    ? '50px'
                    : '20px'
              }}
            >
              <Box>
                <Link href="/">
                  <Image
                    alt="Kroemtech"
                    src={KroemtechLogo}
                    priority
                    width={
                      screenDimensions.width > breakpoints.mobileSmall
                        ? 169
                        : 140
                    }
                    height={
                      screenDimensions.width > breakpoints.mobileSmall ? 30 : 25
                    }
                  />
                </Link>
                {/* Deliberately untranslated: the slogan reads the same in
                    both locales. */}
                <Typography
                  sx={{
                    fontSize: 10,
                    fontStyle: 'italic',
                    color: themeColors.bg.start,
                    textAlign: 'right',
                    whiteSpace: 'nowrap'
                  }}
                >
                  OT security and industrial connectivity
                </Typography>
              </Box>
            </Box>
            <Box sx={classes.linksBox}>
              {screenDimensions.width >= breakpoints.desktopToMobile ? (
                <>
                  <Box
                    sx={classes.flexBox}
                    style={{
                      marginRight:
                        screenDimensions.width <= 1500 ? '10px' : '50px'
                    }}
                  >
                    <MainMenu locale={currentLocale} />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      marginRight:
                        screenDimensions.width <= 1500 ? '10px' : '50px'
                    }}
                  >
                    {/* The account menu and cart lived here. Their pages are
                        no longer part of the site, so they are not rendered. */}
                    {getLangButtons()}
                  </Box>
                </>
              ) : (
                <MenuIcon
                  onClick={() => setDrawerOpen(true)}
                  sx={classes.menuIcon}
                />
              )}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  )
}
